import React, { useState, useEffect, useMemo } from "react";
import { Clock } from "lucide-react";

/**
 * Business hours model — uses Lisbon local time.
 * Weekday 0 = Sunday ... 6 = Saturday
 *   Mon-Fri: 09:00–13:00 and 14:30–18:30
 *   Saturday: 09:00–13:00
 *   Sunday/holidays: closed
 */
const SCHEDULE = {
  1: [["09:00", "13:00"], ["14:30", "18:30"]], // Mon
  2: [["09:00", "13:00"], ["14:30", "18:30"]],
  3: [["09:00", "13:00"], ["14:30", "18:30"]],
  4: [["09:00", "13:00"], ["14:30", "18:30"]],
  5: [["09:00", "13:00"], ["14:30", "18:30"]],
  6: [["09:00", "13:00"]], // Sat
  0: [], // Sun
};

function toMinutes(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

/** Lisbon-time Date snapshot. Falls back to local time if Intl unavailable. */
function getLisbonNow() {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Lisbon",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(new Date());
    const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    return {
      weekday: weekdayMap[map.weekday] ?? new Date().getDay(),
      minutes: Number(map.hour) * 60 + Number(map.minute),
    };
  } catch {
    const d = new Date();
    return { weekday: d.getDay(), minutes: d.getHours() * 60 + d.getMinutes() };
  }
}

function computeStatus() {
  const { weekday, minutes } = getLisbonNow();
  const slots = SCHEDULE[weekday] || [];

  for (const [open, close] of slots) {
    const openMin = toMinutes(open);
    const closeMin = toMinutes(close);
    if (minutes >= openMin && minutes < closeMin) {
      return { open: true, closesAt: close, opensAt: null };
    }
  }

  // Find next opening — same day later, or next day
  const sameDayLater = slots.find(([open]) => toMinutes(open) > minutes);
  if (sameDayLater) {
    return { open: false, closesAt: null, opensAt: sameDayLater[0], opensLabel: "hoje" };
  }

  // Otherwise look forward up to 7 days
  for (let i = 1; i <= 7; i++) {
    const day = (weekday + i) % 7;
    const next = SCHEDULE[day] || [];
    if (next.length > 0) {
      const labels = ["domingo", "amanhã", "terça", "quarta", "quinta", "sexta", "sábado"];
      const label =
        i === 1
          ? "amanhã"
          : ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"][day];
      return { open: false, closesAt: null, opensAt: next[0][0], opensLabel: label };
    }
  }
  return { open: false, closesAt: null, opensAt: null };
}

export const OpenStatusBar = () => {
  const [tick, setTick] = useState(0);

  // Re-evaluate every 60s so the banner stays current as time passes
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const status = useMemo(() => computeStatus(), [tick]);

  return (
    <div
      data-testid="open-status-bar"
      className="w-full glass-soft border-b border-white/[0.06] text-white"
    >
      <div className="w-full px-6 md:px-12 lg:px-20 flex flex-wrap items-center justify-center md:justify-between gap-x-6 gap-y-1 h-9 text-[11px] md:text-xs font-display uppercase tracking-wider">
        {/* Status */}
        <div className="flex items-center gap-2">
          <span className="relative inline-flex h-2.5 w-2.5">
            {status.open ? (
              <>
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span
                  className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400"
                  style={{ boxShadow: "0 0 8px rgba(52, 211, 153, 0.8)" }}
                />
              </>
            ) : (
              <span
                className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF5A00] pulse-soft"
                style={{ boxShadow: "0 0 6px rgba(255, 90, 0, 0.6)" }}
              />
            )}
          </span>
          <span
            data-testid="open-status-text"
            className={status.open ? "text-emerald-400" : "text-[#FF8534]"}
          >
            {status.open
              ? `Aberto agora · Fechamos às ${status.closesAt}`
              : status.opensAt
              ? `Fechado · Abrimos ${status.opensLabel} às ${status.opensAt}`
              : "Fechado"}
          </span>
        </div>

        {/* Schedule (hidden on small screens to save space) */}
        <div className="hidden md:flex items-center gap-2 text-white/60">
          <Clock className="w-3.5 h-3.5" />
          <span>Seg–Sex 09:00–13:00 · 14:30–18:30 · Sáb 09:00–13:00</span>
        </div>
      </div>
    </div>
  );
};

export default OpenStatusBar;
